import {
  KEY_STORAGE_TOKEN,
  KEY_STORAGE_WP_TOKEN,
  COUNT_DAYS_SAVE_COOKIE,
  KEY_USER_UUID,
  KEY_SESSION_UUID,
  REGISTRATION_TYPE,
  KEY_STORAGE_TOKEN_EXP_AT,
  PTP_IGNORED_ALERT,
  SESSION_PHONE_NUMBER,
  OTP_RESEND_STATE,
  OTP_REMAINING_STATE,
  OTP_LIM_KEY,
  IMPERSONATE_ADMIN_ID,
  KEY_STORAGE_TARGET_URL_LOCK_WRITE,
  SESSION_STORAGE_IMPERSONATE_ACTIVE,
  VERIF_WAIT_TIME,
  AUTH_METHOD,
  LOGIN_OTP_SESSION_UNTIL,
  PIN_DIALOG_SKIPPED_SESSION,
  IMPERSONATE_COOKIE_FROM_CRM,
  CAMERA_PERMISSION_ALLOW_POPUP,
} from '../constants';
import dayjs from 'dayjs';
import { removeCookie, setCookie } from './cookies';
import { addDaysDate } from './functions';
import { clearUserTrackingCookies } from './userTrackingCookies';
import { clearSentryContext } from './sentry';
import { getJwtExpirationIso } from './jwt';

const countDay = addDaysDate(COUNT_DAYS_SAVE_COOKIE);
// TODO: use it for input names, add keys from other form pages
const formInputKeys = {
  dni_expire_at: '',
  dni_number: '',
  marital_status: '',
  mother_surname: '',
  first_name: '',
  father_surname: '',
  gender: '',
  birthday: '',
};
const marketingUtmKeys = {
  utm_source: '',
  utm_medium: '',
  utm_campaign: '',
};

export const setStorageToken = (token: string) => {
  localStorage.setItem(KEY_STORAGE_TOKEN, token);
  setCookie(KEY_STORAGE_WP_TOKEN, token, countDay);
};
export const getStorageToken = () => localStorage.getItem(KEY_STORAGE_TOKEN);
export const removeStorageToken = () => {
  const countDay = addDaysDate(-1);
  localStorage.removeItem(KEY_STORAGE_TOKEN);
  setCookie(KEY_STORAGE_WP_TOKEN, '', countDay);
};

export const setStorageTokenExpAt = (token: string) => {
  localStorage.setItem(KEY_STORAGE_TOKEN_EXP_AT, token);
};
export const getStorageTokenExpAt = () => localStorage.getItem(KEY_STORAGE_TOKEN_EXP_AT);
export const removeStorageTokenExpAt = () => {
  localStorage.removeItem(KEY_STORAGE_TOKEN_EXP_AT);
};
export const hasStorageTokenExpAt = (): boolean => Boolean(getStorageTokenExpAt());

/**
 * Persist JWT expiry: prefer API `jwt_token_expired_at`, otherwise derive from JWT `exp`.
 * Needed so axios/isAlreadyAuthorized can block expired sessions without a round-trip.
 */
export const persistTokenExpiration = (token: string | null | undefined, expiredAtFromApi?: string | null): void => {
  if (expiredAtFromApi) {
    setStorageTokenExpAt(expiredAtFromApi);
    return;
  }

  if (!token) {
    return;
  }

  const fromJwt = getJwtExpirationIso(token);
  if (fromJwt) {
    setStorageTokenExpAt(fromJwt);
  }
};

export const isStorageTokenExpired = (): boolean => {
  const expAt = getStorageTokenExpAt();
  if (expAt) {
    return dayjs(expAt).isBefore(dayjs());
  }

  const token = getStorageToken();
  if (!token) {
    return false;
  }

  const fromJwt = getJwtExpirationIso(token);
  if (!fromJwt) {
    return false;
  }

  return dayjs(fromJwt).isBefore(dayjs());
};

export const clearStorageSession = () => {
  removeStorageToken();
  removeUserUUIDToken();
  removeSessionUUIDToken();
  removeStorageTokenExpAt();
  removeStorageItem(SESSION_PHONE_NUMBER);
  removeStorageItem(OTP_RESEND_STATE);
  removeStorageItem(OTP_REMAINING_STATE);
  removeStorageItem(OTP_LIM_KEY);
  removePtpIgnored();
  removeRegTypeItem();
  removeImpAdminId();
  removeStorageItem(VERIF_WAIT_TIME);
  removeStorageItems(formInputKeys);
  removeStorageItems(marketingUtmKeys);
  removeStorageItem(PIN_DIALOG_SKIPPED_SESSION);
  removeStorageItem(AUTH_METHOD);
  removeStorageItem(LOGIN_OTP_SESSION_UNTIL);
  removeSessionItem(SESSION_STORAGE_IMPERSONATE_ACTIVE);
  removeCookie(KEY_STORAGE_TARGET_URL_LOCK_WRITE);
  removeCookie(IMPERSONATE_COOKIE_FROM_CRM);
  clearUserTrackingCookies();
  removeStorageItem(CAMERA_PERMISSION_ALLOW_POPUP);
  clearSentryContext();
};

export const setStorageItem = (key: string, value: any) => {
  localStorage.setItem(key, value);
};
export const getStorageItem = (key: string): string => {
  return localStorage.getItem(key) ?? '';
};
export const removeStorageItems = (values: any) => {
  const keys = Object.keys(values);
  keys.forEach((item: string) => {
    localStorage.removeItem(item);
  });
};

export const removeStorageItem = (key: string) => {
  localStorage.removeItem(key);
};

export const setUserUUIDToken = (token: string) => {
  localStorage.setItem(KEY_USER_UUID, token);
};

export const getUserUUIDToken = () => {
  return localStorage.getItem(KEY_USER_UUID) ?? '';
};
export const removeUserUUIDToken = () => {
  localStorage.removeItem(KEY_USER_UUID);
};

export const setRegTypeItem = (token: string) => {
  localStorage.setItem(REGISTRATION_TYPE, token);
};

export const getRegTypeItem = () => {
  return localStorage.getItem(REGISTRATION_TYPE) ?? '';
};
export const removeRegTypeItem = () => {
  localStorage.removeItem(REGISTRATION_TYPE);
};

export const setSessionUUIDToken = (token: string) => {
  localStorage.setItem(KEY_SESSION_UUID, token);
};

export const getSessionUUIDToken = () => {
  return localStorage.getItem(KEY_SESSION_UUID);
};

export const removeSessionUUIDToken = () => {
  localStorage.removeItem(KEY_SESSION_UUID);
};

export const setSessionItem = (key: string, value: any) => {
  sessionStorage.setItem(key, value);
};
export const getSessionItem = (key: string): string => {
  return sessionStorage.getItem(key) ?? '';
};
export const removeSessionItem = (key: string) => {
  sessionStorage.removeItem(key);
};

export const setPtpIgnored = () => {
  localStorage.setItem(PTP_IGNORED_ALERT, 'show');
};
export const getPtpIgnored = () => {
  return localStorage.getItem(PTP_IGNORED_ALERT) ?? '';
};
export const removePtpIgnored = () => {
  localStorage.removeItem(PTP_IGNORED_ALERT);
};

export const setImpAdminId = (value: string) => {
  localStorage.setItem(IMPERSONATE_ADMIN_ID, value);
};
export const getImpAdminId = () => {
  return localStorage.getItem(IMPERSONATE_ADMIN_ID) ?? '';
};
export const removeImpAdminId = () => {
  localStorage.removeItem(IMPERSONATE_ADMIN_ID);
};
